import React from 'react';
import { Translate } from 'react-localize-redux';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getIsAgency } from '../../../../selectors/user';
import {
    partnerShape,
    eventShape,
    siteShape,
    templateShape,
    TemplateTypes,
    organizationOptionsShape,
} from '../../types';
import css from './EventsDataSection.css';
import EventPostbacksTable from './EventPostbacksTable';
import { getKeyByValue } from '../../../../global/utils';
import { POSTBACK_FILTERS_TYPES } from '../advancedPostbackSettings/PostbackFilters';

function EventPostbacksData({
    partner,
    site,
    template = null,
    events = [],
    allowedEvents = null,
    configuredEvents = null,
    extraTableCells = [],
    postbackExtraFields = {},
    tableDataTestId = '',
    templateType = null,
    setPostbacks = () => {},
    setTouched = () => {},
    errors = [],
    touched,
    isReadonly,
    orgOptions,
    availableFilters,
}) {
    const isAgency = useSelector(getIsAgency);
    const templateTypeValue = getKeyByValue(TemplateTypes, templateType);
    const missingTemplateText = `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.NOT_SUPPORTED_${templateTypeValue}_POSTBACKS`;
    const missingEventsText = isAgency
        ? 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.NOT_RECEIVING_EVENTS_AGENCY'
        : `STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.NOT_RECEIVING_${templateTypeValue}_EVENTS`;

    // Render table if we have configured events OR allowed events
    // This allows showing existing configured events even when they're no longer in allowed list
    const hasEventsToShow = events.length > 0 || configuredEvents?.length > 0;

    return (
        <>
            {configuredEvents && template && hasEventsToShow ? (
                <>
                    <EventPostbacksTable
                        availableFilters={availableFilters}
                        eventsPostbacks={configuredEvents}
                        partner={partner}
                        events={events}
                        allowedEvents={allowedEvents || events}
                        template={template}
                        setPostbacks={setPostbacks}
                        setTouched={setTouched}
                        extraTableCells={extraTableCells}
                        postbackExtraFields={postbackExtraFields}
                        tableDataTestId={tableDataTestId}
                        errors={errors}
                        touched={touched}
                        isReadonly={isReadonly}
                        orgOptions={orgOptions}
                    />
                    <div className={css.comment}>
                        <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.IN_APP_EVENTS.AUTO_GENERATED_EVENTS_NOTE" />
                    </div>
                </>
            ) : null}
            {template ? null : (
                <div className={css.comment}>
                    <Translate
                        id={missingTemplateText}
                        data={{ partnerName: partner.displayName, platform: site.platform }}
                    />
                </div>
            )}
            {hasEventsToShow ? null : (
                <div className={css.comment}>
                    <Translate id={missingEventsText} />
                </div>
            )}
        </>
    );
}

EventPostbacksData.propTypes = {
    partner: partnerShape.isRequired,
    site: siteShape.isRequired,
    orgOptions: organizationOptionsShape.isRequired,
    template: templateShape,
    events: PropTypes.arrayOf(eventShape),
    allowedEvents: PropTypes.arrayOf(eventShape),
    configuredEvents: PropTypes.arrayOf(PropTypes.object),
    setPostbacks: PropTypes.func,
    extraTableCells: PropTypes.arrayOf(PropTypes.object),
    postbackExtraFields: PropTypes.object,
    tableDataTestId: PropTypes.string,
    templateType: PropTypes.oneOf(Object.values(TemplateTypes)),
    setTouched: PropTypes.func,
    errors: PropTypes.arrayOf(PropTypes.object),
    touched: PropTypes.arrayOf(PropTypes.object),
    isReadonly: PropTypes.bool,
    availableFilters: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            type: PropTypes.oneOf(Object.values(POSTBACK_FILTERS_TYPES)),
        })
    ),
};

export default EventPostbacksData;
